import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react'

interface MapCenter {
  lat: number
  lng: number
}

interface MapCenterContextValue {
  getCenter: (key: string) => MapCenter | null
  setCenter: (key: string, center: MapCenter | null) => void
}

const MapCenterContext = createContext<MapCenterContextValue | undefined>(undefined)

interface MapCenterProviderProps {
  children: ReactNode
}

export const MapCenterProvider = ({ children }: MapCenterProviderProps) => {
  const [centers, setCenters] = useState<Record<string, MapCenter | null>>({})

  const getCenter = useCallback(
    (key: string) => {
      return centers[key] ?? null
    },
    [centers],
  )

  const setCenter = useCallback((key: string, newCenter: MapCenter | null) => {
    setCenters(prev => ({
      ...prev,
      [key]: newCenter,
    }))
  }, [])

  return (
    <MapCenterContext.Provider value={{ getCenter, setCenter }}>
      {children}
    </MapCenterContext.Provider>
  )
}

export const useMapCenter = (key: string) => {
  const context = useContext(MapCenterContext)
  if (context === undefined) {
    throw new Error('useMapCenter must be used within a MapCenterProvider')
  }

  const center = context.getCenter(key)
  const setCenterForKey = useCallback(
    (newCenter: MapCenter | null) => {
      context.setCenter(key, newCenter)
    },
    [context, key],
  )

  return {
    center,
    setCenter: setCenterForKey,
  }
}
